import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import './IncomeStatementWidget.css';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import useContainerWidth from '../../hooks/useContainerWidth';
import {
  BaseWidgetProps,
  CommonTooltipType,
  WidgetHeightMessage,
} from '../../types/common.type';
import {
  formatNumber,
  formatKMBT,
  getCssVariable,
  getDecimalPlaces,
} from '../../utils/utils';
import { Region, ThemeType, WidgetNameSource } from '../../utils/type';
import Loading from '../../Components/Loading/Loading';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';
import moment from 'moment';
import { getIncomeStatement } from '../../api/company.api';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down.svg';
import ReactECharts from 'echarts-for-react';
import { FiscalYear, WidgetMessageType } from '../../utils/type';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
export interface IncomeStatementWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  select?: FiscalYear;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

interface IncomeStatementData {
  data_index: number;
  ric: string;
  period_end_date: string;
  fiscal_year: number;
  dividend: number;
  netIncomeBeforeTaxes: number;
  totalOperatingExpense: number;
  totalRevenue: number;
  basicEPS: number;
  costofOperatingRevenue: number;
  dilutedEPS: number;
  grossProfit: number;
  operatingProfit: number;
  netIncomeAfterTaxes: number;
  taxes: number;
  EBIT: number;
  EBITA: number;
  SNIN: number | null;
  SONT: number | null;
  nonRecurringIncomeOrExpense: number | null;
  curr_fin: string | null;
  // pre-computed growth fields (percentage, e.g. 13.5 means +13.5%)
  totalRevenueGrowth?: number | null;
  grossProfitGrowth?: number | null;
  operatingProfitGrowth?: number | null;
  netIncomeBeforeTaxesGrowth?: number | null;
  netIncomeAfterTaxesGrowth?: number | null;
}

interface ExpandedRows {
  [key: string]: boolean;
}

const IncomeStatementWidget = (props: IncomeStatementWidgetProps) => {
  const { onHeightChange } = props;

  const theme = props.theme ?? ThemeType.Direct;
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'incomeStatement');
  const widgetRef = useRef<HTMLDivElement | null>(null);
  useResizeNotifier(
    widgetRef,
    WidgetNameSource.IncomeStatement,
    onHeightChange,
  );
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const { isSmallScreen } = useContainerWidth(widgetRef);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [currFin, setCurrFin] = useState<string | null>(null);
  const [data, setData] = useState<IncomeStatementData[]>([]);
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>({});
  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // Filter data for small screen - show only selected fiscal year
  const displayData = useMemo(() => {
    if (!isSmallScreen || data.length === 0) {
      return data.length > 5 ? data.slice(-5) : data;
    }
    const dataIndexMap: { [key: string]: number } = {
      [FiscalYear.Latest]: 1,
      [FiscalYear.Prior1Year]: 2,
      [FiscalYear.Prior2Years]: 3,
      [FiscalYear.Prior3Years]: 4,
      [FiscalYear.Prior4Years]: 5,
      [FiscalYear.Prior5Years]: 6,
    };
    const targetIndex = dataIndexMap[props.select ?? FiscalYear.Latest];
    const found = data.find((item) => item.data_index === targetIndex);
    return [found ?? data[0]];
  }, [data, isSmallScreen, props.select]);

  // Hide tooltip on scroll
  useEffect(() => {
    const handleScroll = () => {
      setTooltip((prev) => ({ ...prev, show: false }));
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  // Call API when props changes

  // Calculate growth percentage
  const calculateGrowth = (
    current: number,
    previous: number | null,
  ): number | null => {
    if (previous === null || previous === 0) return null;
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  // Format value to raw value
  const formatMillion = (value: number | null | undefined): number | null => {
    if (value === null || value === undefined) return null;
    return value * 1000000;
  };
  // Format growth percentage
  const formatGrowth = (
    growth: number | null,
  ): { value: string; isPositive: boolean } => {
    if (growth === null) return { value: '-', isPositive: true };
    const formatted = formatNumber(
      growth.toString(),
      2,
      2,
      false,
      true,
      false,
      '%',
      '-',
    );
    return { value: formatted, isPositive: growth >= 0 };
  };

  // Bar chart config for Total revenue, Operating profit, Net income
  const barChartOption = useMemo(() => {
    if (!data || data.length === 0) return {};

    // x-axis labels (fiscal years)
    const years = data.map((item) => item.fiscal_year.toString());

    const totalRevenueSeries = data.map((item) =>
      formatMillion(item.totalRevenue as number | null),
    );
    const operatingProfitSeries = data.map((item) =>
      formatMillion(item.operatingProfit as number | null),
    );
    const netIncomeSeries = data.map((item) =>
      formatMillion(item.netIncomeAfterTaxes as number | null),
    );

    return {
      tooltip: {
        appendTo: 'body',
        confine: true,
        margin: 20,
        outside: false,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any[]) => {
          if (!params || !params.length) return '';
          const yearLabel = params[0].axisValue;
          const lines = params
            .filter((p) => p.value !== null && p.value !== undefined)
            .map((p) => {
              const valStr = `${formatKMBT(p.value.toString(), 1, 1, false).value}${formatKMBT(p.value.toString(), 1, 1, false).suffix}`;
              return `${p.marker} ${p.seriesName}: ${valStr}`;
            });
          return [yearLabel, ...lines].join('<br/>');
        },
      },
      legend: {
        bottom: 0,
        left: 'center',
        data: ['Total revenue', 'Operating profit', 'Net income'],
        textStyle: {
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          fontSize: 10,
          color: '#3C3C4380',
        },
        itemWidth: 12,
        itemHeight: 8,
      },
      grid: {
        left: 8,
        right: 36,
        top: 20,
        bottom: 50,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        axisLine: {
          lineStyle: { color: '#E0E0E0' },
        },
        axisTick: { show: false },
        axisLabel: {
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          fontSize: 10,
          color: '#3C3C4380',
        },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        axisLine: {
          lineStyle: { color: '#E0E0E0' },
        },
        splitLine: {
          lineStyle: { color: '#F0F0F0' },
        },
        axisLabel: {
          interval: 0,
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          fontSize: 10,
          color: '#3C3C4380',
          formatter: (val: number) => {
            if (val === 0) return '0';
            return `${formatKMBT(val.toString(), 1, 1, false).value}${formatKMBT(val.toString(), 1, 1, false).suffix}`;
          },
        },
      },
      series: [
        {
          name: 'Total revenue',
          type: 'bar',
          minBarWidth: 10,
          maxBarWidth: 30,
          itemStyle: {
            color: getCssVariable('--chart-primary', theme),
          },
          label: {
            show: !isSmallScreen,
            position: 'top',
            formatter: (p: any) =>
              p.value !== null && p.value !== undefined
                ? `${formatKMBT(p.value.toString(), 1, 1, false).value}${formatKMBT(p.value.toString(), 1, 1, false).suffix}`
                : '',
            fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
            fontSize: 10,
            color: '#3C3C4380',
          },
          data: totalRevenueSeries,
        },
        {
          name: 'Operating profit',
          type: 'bar',
          minBarWidth: 10,
          maxBarWidth: 30,
          itemStyle: {
            color: getCssVariable('--chart-secondary', theme),
          },
          label: {
            show: !isSmallScreen,
            position: 'top',
            formatter: (p: any) =>
              p.value !== null && p.value !== undefined
                ? `${formatKMBT(p.value.toString(), 1, 1, false).value}${formatKMBT(p.value.toString(), 1, 1, false).suffix}`
                : '',
            fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
            fontSize: 10,
            color: '#3C3C4380',
          },
          data: operatingProfitSeries,
        },
        {
          name: 'Net income',
          type: 'bar',
          minBarWidth: 10,
          maxBarWidth: 30,
          itemStyle: {
            color: getCssVariable('--chart-tertiary', theme),
          },
          label: {
            show: !isSmallScreen,
            position: 'top',
            formatter: (p: any) =>
              p.value !== null && p.value !== undefined
                ? `${formatKMBT(p.value.toString(), 1, 1, false).value}${formatKMBT(p.value.toString(), 1, 1, false).suffix}`
                : '',
            fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
            fontSize: 10,
            color: '#3C3C4380',
          },
          data: netIncomeSeries,
        },
      ],
    };
  }, [data, isSmallScreen, theme]);

  const fetchIncomeStatementData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res: any = await getIncomeStatement(
        props.symbol || '',
        props.token || '',
      );
      if (res) {
        //find curr_fin from res.data.
        const curr_fin = res.data.find(
          (item: any) => item.curr_fin !== null,
        )?.curr_fin;
        setCurrFin(curr_fin);
        // 1) Sort all years by fiscal year (ascending)
        const sortedData: IncomeStatementData[] = [...res.data].sort(
          (a: IncomeStatementData, b: IncomeStatementData) =>
            a.fiscal_year - b.fiscal_year,
        );

        // 2) Pre-compute growth using full history (before limiting to 5 years)
        const enrichedData: IncomeStatementData[] = sortedData.map(
          (item, index, arr) => {
            const prev = index > 0 ? arr[index - 1] : null;

            const totalRevenueGrowth = prev
              ? calculateGrowth(item.totalRevenue, prev.totalRevenue)
              : null;
            const grossProfitGrowth = prev
              ? calculateGrowth(item.grossProfit, prev.grossProfit)
              : null;
            const operatingProfitGrowth = prev
              ? calculateGrowth(item.operatingProfit, prev.operatingProfit)
              : null;
            const netIncomeBeforeTaxesGrowth = prev
              ? calculateGrowth(
                  item.netIncomeBeforeTaxes,
                  prev.netIncomeBeforeTaxes,
                )
              : null;
            const netIncomeAfterTaxesGrowth = prev
              ? calculateGrowth(
                  item.netIncomeAfterTaxes,
                  prev.netIncomeAfterTaxes,
                )
              : null;

            return {
              ...item,
              totalRevenueGrowth,
              grossProfitGrowth,
              operatingProfitGrowth,
              netIncomeBeforeTaxesGrowth,
              netIncomeAfterTaxesGrowth,
            };
          },
        );

        setData(enrichedData);
        setIsEmptyData(enrichedData.length === 0);
        if (enrichedData.length === 0) {
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'IncomeStatementWidget',
            data: null,
          });
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch income statement data');
      console.error('Error fetching income statement data:', err);
      props.onError?.({
        message: err.message,
        type: err.name + '_' + err.code,
      });
      setIsEmptyData(true);
    } finally {
      setIsLoading(false);
    }
  }, [props.token, props.symbol]);

  useEffect(() => {
    fetchIncomeStatementData();
  }, [props.token, props.symbol, fetchIncomeStatementData]);

  // Tooltip positioning
  const constrainTooltipPosition = (e: React.MouseEvent) => {
    let x = e.pageX + 15;
    let y = e.pageY;

    const elementMaxWidth = tooltipRef.current?.clientWidth || 0;
    const elementMaxHeight = tooltipRef.current?.clientHeight || 0;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const viewportWidth = window.innerWidth + scrollX;
    const viewportHeight = window.innerHeight + scrollY;

    if (x + elementMaxWidth > viewportWidth) {
      x = x - elementMaxWidth;
    }
    if (y + elementMaxHeight > viewportHeight) {
      y = y - elementMaxHeight;
    }

    if (x < 0) {
      x = 10;
    }

    return { x, y };
  };

  // Toggle row expansion
  const toggleRow = (rowKey: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  return (
    <Layout theme={theme}>
      <div ref={widgetRef} className="inst-container">
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="inst-content-wrapper">
            <div className="inst-chart-container">
              {data && data.length > 0 && (
                <ReactECharts
                  option={barChartOption}
                  style={{
                    width: '100%',
                    height: 180,
                    marginTop: 20,
                    paddingTop: 20,
                  }}
                />
              )}
            </div>
            <div ref={tableContainerRef} className="inst-table-container">
              <table className="inst-table">
                <thead>
                  <tr>
                    <th></th>
                    <th className="inst-table-header-label">
                      <div className="inst-currency-label">
                        Unit million ({currFin ?? ''})
                      </div>
                    </th>
                    {displayData.map((item) => (
                      <th
                        key={item.fiscal_year}
                        className="inst-table-header-year"
                      >
                        <div className="inst-header-content">
                          <div className="inst-header-fy">
                            FY{item.fiscal_year}
                          </div>
                          <div className="inst-header-date">
                            {moment(item.period_end_date).format('DD MMM YYYY')}
                          </div>
                        </div>
                      </th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Total revenue */}
                  <tr className="inst-table-row">
                    <td></td>
                    <td className="inst-table-label-cell">
                      <div
                        className="inst-label-with-icon"
                        onClick={() => toggleRow('totalRevenue')}
                      >
                        <ArrowDownIcon
                          className={`inst-expand-icon ${expandedRows['totalRevenue'] ? 'expanded' : ''}`}
                        />
                        <span
                          className="inst-label-text"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Total revenue',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Total revenue
                        </span>
                      </div>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.totalRevenue !== null &&
                        item.totalRevenue !== undefined
                          ? formatNumber(item.totalRevenue.toString(), dp, dp)
                          : '-';
                      const growthFormatted =
                        item.totalRevenueGrowth !== undefined &&
                        item.totalRevenueGrowth !== null
                          ? formatGrowth(item.totalRevenueGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="inst-table-cell">
                          <div className="inst-cell-content">
                            <span className="inst-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`inst-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Expanded rows for Total revenue */}
                  {expandedRows['totalRevenue'] && (
                    <>
                      <tr className="inst-table-row inst-expanded-row">
                        <td></td>
                        <td
                          className="inst-table-label-cell inst-indented-label"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Cost of operating revenue',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Cost of operating revenue
                        </td>
                        {displayData.map((item) => {
                          const dp = getDecimalPlaces(item.curr_fin);
                          const displayValue =
                            item.costofOperatingRevenue !== null &&
                            item.costofOperatingRevenue !== undefined
                              ? formatNumber(
                                  item.costofOperatingRevenue.toString(),
                                  dp,
                                  dp,
                                )
                              : '-';
                          return (
                            <td
                              key={item.fiscal_year}
                              className="inst-table-cell"
                            >
                              <div className="inst-cell-content">
                                <span className="inst-cell-value">
                                  {displayValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                    </>
                  )}

                  {/* Gross profit */}
                  <tr className="inst-table-row">
                    <td></td>
                    <td className="inst-table-label-cell">
                      <div
                        className="inst-label-with-icon"
                        onClick={() => toggleRow('grossProfit')}
                      >
                        <ArrowDownIcon
                          className={`inst-expand-icon ${expandedRows['grossProfit'] ? 'expanded' : ''}`}
                        />
                        <span
                          className="inst-label-text"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Gross profit',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Gross profit
                        </span>
                      </div>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.grossProfit !== null &&
                        item.grossProfit !== undefined
                          ? formatNumber(item.grossProfit.toString(), dp, dp)
                          : '-';
                      const growthFormatted =
                        item.grossProfitGrowth !== undefined &&
                        item.grossProfitGrowth !== null
                          ? formatGrowth(item.grossProfitGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="inst-table-cell">
                          <div className="inst-cell-content">
                            <span className="inst-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`inst-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Expanded rows for Gross profit */}
                  {expandedRows['grossProfit'] && (
                    <>
                      <tr className="inst-table-row inst-expanded-row">
                        <td></td>
                        <td
                          className="inst-table-label-cell inst-indented-label"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Operating expenses',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Operating expenses
                        </td>
                        {displayData.map((item) => {
                          const dp = getDecimalPlaces(item.curr_fin);
                          const displayValue =
                            item.totalOperatingExpense !== null &&
                            item.totalOperatingExpense !== undefined
                              ? formatNumber(
                                  item.totalOperatingExpense.toString(),
                                  dp,
                                  dp,
                                )
                              : '-';
                          return (
                            <td
                              key={item.fiscal_year}
                              className="inst-table-cell"
                            >
                              <div className="inst-cell-content">
                                <span className="inst-cell-value">
                                  {displayValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                    </>
                  )}

                  {/* Operating profit */}
                  <tr className="inst-table-row">
                    <td></td>
                    <td className="inst-table-label-cell">
                      <div
                        className="inst-label-with-icon"
                        onClick={() => toggleRow('operatingProfit')}
                      >
                        <ArrowDownIcon
                          className={`inst-expand-icon ${expandedRows['operatingProfit'] ? 'expanded' : ''}`}
                        />
                        <span
                          className="inst-label-text"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Operating profit',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Operating profit
                        </span>
                      </div>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.operatingProfit !== null &&
                        item.operatingProfit !== undefined
                          ? formatNumber(
                              item.operatingProfit.toString(),
                              dp,
                              dp,
                            )
                          : '-';
                      const growthFormatted =
                        item.operatingProfitGrowth !== undefined &&
                        item.operatingProfitGrowth !== null
                          ? formatGrowth(item.operatingProfitGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="inst-table-cell">
                          <div className="inst-cell-content">
                            <span className="inst-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`inst-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Expanded rows for Operating profit */}
                  {expandedRows['operatingProfit'] && (
                    <>
                      <tr className="inst-table-row inst-expanded-row">
                        <td></td>
                        <td
                          className="inst-table-label-cell inst-indented-label"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Non-recurring income/expense',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Non-recurring income/expen...
                        </td>
                        {displayData.map((item) => {
                          const dp = getDecimalPlaces(item.curr_fin);
                          const value = item.nonRecurringIncomeOrExpense;
                          const displayValue =
                            value !== null && value !== undefined
                              ? formatNumber(value.toString(), dp, dp)
                              : '-';
                          return (
                            <td
                              key={item.fiscal_year}
                              className="inst-table-cell"
                            >
                              <div className="inst-cell-content">
                                <span className="inst-cell-value">
                                  {displayValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                    </>
                  )}

                  {/* Net income before taxes */}
                  <tr className="inst-table-row">
                    <td></td>
                    <td className="inst-table-label-cell">
                      <div
                        className="inst-label-with-icon"
                        onClick={() => toggleRow('netIncomeBeforeTaxes')}
                      >
                        <ArrowDownIcon
                          className={`inst-expand-icon ${expandedRows['netIncomeBeforeTaxes'] ? 'expanded' : ''}`}
                        />
                        <span
                          className="inst-label-text"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Net income before taxes',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Net income before taxes
                        </span>
                      </div>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.netIncomeBeforeTaxes !== null &&
                        item.netIncomeBeforeTaxes !== undefined
                          ? formatNumber(
                              item.netIncomeBeforeTaxes.toString(),
                              dp,
                              dp,
                            )
                          : '-';
                      const growthFormatted =
                        item.netIncomeBeforeTaxesGrowth !== undefined &&
                        item.netIncomeBeforeTaxesGrowth !== null
                          ? formatGrowth(item.netIncomeBeforeTaxesGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="inst-table-cell">
                          <div className="inst-cell-content">
                            <span className="inst-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`inst-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Expanded rows for Net income before taxes */}
                  {expandedRows['netIncomeBeforeTaxes'] && (
                    <>
                      <tr className="inst-table-row inst-expanded-row">
                        <td></td>
                        <td
                          className="inst-table-label-cell inst-indented-label"
                          onMouseEnter={(e) => {
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip({
                              show: true,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                              content: 'Taxes',
                            });
                          }}
                          onMouseMove={(e) => {
                            if (e.movementX === 0 && e.movementY === 0) {
                              return;
                            }
                            const constrainedPosition =
                              constrainTooltipPosition(e);
                            setTooltip((prev) => ({
                              ...prev,
                              x: constrainedPosition.x,
                              y: constrainedPosition.y,
                            }));
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                          onMouseOut={() => {
                            setTooltip((prev) => ({ ...prev, show: false }));
                          }}
                        >
                          Taxes
                        </td>
                        {displayData.map((item) => {
                          const dp = getDecimalPlaces(item.curr_fin);
                          const displayValue =
                            item.taxes !== null && item.taxes !== undefined
                              ? formatNumber(item.taxes.toString(), dp, dp)
                              : '-';
                          return (
                            <td
                              key={item.fiscal_year}
                              className="inst-table-cell"
                            >
                              <div className="inst-cell-content">
                                <span className="inst-cell-value">
                                  {displayValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                    </>
                  )}

                  {/* Net income after taxes */}
                  <tr className="inst-table-row">
                    <td></td>
                    <td className="inst-table-label-cell">
                      <span
                        className="inst-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'Net income after taxes',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        Net income after taxes
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.netIncomeAfterTaxes !== null &&
                        item.netIncomeAfterTaxes !== undefined
                          ? formatNumber(
                              item.netIncomeAfterTaxes.toString(),
                              dp,
                              dp,
                            )
                          : '-';
                      const growthFormatted =
                        item.netIncomeAfterTaxesGrowth !== undefined &&
                        item.netIncomeAfterTaxesGrowth !== null
                          ? formatGrowth(item.netIncomeAfterTaxesGrowth)
                          : { value: '-', isPositive: true };
                      return (
                        <td key={item.fiscal_year} className="inst-table-cell">
                          <div className="inst-cell-content">
                            <span className="inst-cell-value">
                              {displayValue}
                            </span>
                            {growthFormatted && (
                              <span
                                className={`inst-cell-growth ${growthFormatted.isPositive ? 'positive' : 'negative'}`}
                              >
                                {growthFormatted.value}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Basic EPS */}
                  <tr className="inst-table-row">
                    <td></td>
                    <td className="inst-table-label-cell">
                      <span
                        className="inst-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'Basic EPS',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        Basic EPS
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.basicEPS !== null && item.basicEPS !== undefined
                          ? formatNumber(item.basicEPS.toString(), dp, dp)
                          : '-';
                      return (
                        <td key={item.fiscal_year} className="inst-table-cell">
                          <div className="inst-cell-content">
                            <span className="inst-cell-value">
                              {displayValue}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Diluted EPS */}
                  <tr className="inst-table-row">
                    <td></td>
                    <td className="inst-table-label-cell">
                      <span
                        className="inst-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'Diluted EPS',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        Diluted EPS
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.dilutedEPS !== null &&
                        item.dilutedEPS !== undefined
                          ? formatNumber(item.dilutedEPS.toString(), dp, dp)
                          : '-';
                      return (
                        <td key={item.fiscal_year} className="inst-table-cell">
                          <div className="inst-cell-content">
                            <span className="inst-cell-value">
                              {displayValue}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* Dividend */}
                  <tr className="inst-table-row">
                    <td></td>
                    <td className="inst-table-label-cell">
                      <span
                        className="inst-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'Dividend',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        Dividend
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.dividend !== null && item.dividend !== undefined
                          ? formatNumber(item.dividend.toString(), dp, dp)
                          : '-';
                      return (
                        <td key={item.fiscal_year} className="inst-table-cell">
                          <div className="inst-cell-content">
                            <span className="inst-cell-value">
                              {displayValue}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* EBIT */}
                  <tr className="inst-table-row">
                    <td></td>
                    <td className="inst-table-label-cell">
                      <span
                        className="inst-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'EBIT',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        EBIT
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.EBIT !== null && item.EBIT !== undefined
                          ? formatNumber(item.EBIT.toString(), dp, dp)
                          : '-';
                      return (
                        <td key={item.fiscal_year} className="inst-table-cell">
                          <div className="inst-cell-content">
                            <span className="inst-cell-value">
                              {displayValue}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>

                  {/* EBITA */}
                  <tr className="inst-table-row">
                    <td></td>
                    <td className="inst-table-label-cell">
                      <span
                        className="inst-label-text"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: 'EBITA',
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        EBITA
                      </span>
                    </td>
                    {displayData.map((item) => {
                      const dp = getDecimalPlaces(item.curr_fin);
                      const displayValue =
                        item.EBITA !== null && item.EBITA !== undefined
                          ? formatNumber(item.EBITA.toString(), dp, dp)
                          : '-';
                      return (
                        <td key={item.fiscal_year} className="inst-table-cell">
                          <div className="inst-cell-content">
                            <span className="inst-cell-value">
                              {displayValue}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <CommonTooltip
        ref={tooltipRef}
        content={tooltip.content}
        show={tooltip.show}
        x={tooltip.x}
        y={tooltip.y}
      />
    </Layout>
  );
};

export default IncomeStatementWidget;
